import { getCADchatSpaceLink } from "./helpers";

export type CADchatMeetingStageMode = "new_space" | "existing_space";

export type CADchatMeetingStagePayload = {
  stageId?: string;
  mode: CADchatMeetingStageMode;
  organizationId: string;
  title: string;
  spaceId?: string;
  spaceName?: string;
  organizerEmail?: string;
};

export const CADCHAT_PENDING_MEETING_BLOCK_ID = "cadchat-pending-meeting";
export const CADCHAT_FINAL_MEETING_BLOCK_ID = "cadchat-meeting-information";

/**
 * Key used to store the staging payload in the Outlook item's custom properties.
 *
 * This is the reliable transport between the task pane and the on-send handler. We also embed the
 * payload in the body (data-cadchat-stage) for a visible pending block, but Outlook on the web
 * sanitizes appointment bodies and strips custom data-* attributes, so the body cannot be relied on
 * to carry the payload cross-platform. Custom properties are per-item add-in metadata that survive
 * sanitization and are available in the OnAppointmentSend runtime.
 */
export const CADCHAT_STAGE_CUSTOM_PROPERTY = "cadchatStagePayload";

/**
 * Visible headline text inside the pending block. Outlook on the web strips the block's id, data-*
 * attributes, and HTML comments, but preserves visible text — so this is used as a
 * sanitization-proof anchor to locate and remove the block on send. Keep it in sync with the copy
 * rendered by buildCADchatPendingMeetingBlock.
 */
export const CADCHAT_PENDING_MEETING_HEADLINE = "Meeting ready to create";

const PENDING_BLOCK_REGEX =
  /(?:<!--\s*CADCHAT_PENDING_MEETING\s*-->\s*)?<div[^>]*id=["']cadchat-pending-meeting["'][\s\S]*?<\/div>\s*(?:<!--\s*\/CADCHAT_PENDING_MEETING\s*-->)?/i;

const FINAL_BLOCK_REGEX =
  /<div[^>]*id=["']cadchat-meeting-information["'][\s\S]*?<\/div>/i;

const STAGE_PAYLOAD_REGEX = /data-cadchat-stage=["']([^"']+)["']/i;

export const createCADchatMeetingStageId = () =>
  `cadchat-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const escapeHtml = (value: string | undefined) =>
  (value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

export const encodeCADchatMeetingStagePayload = (payload: CADchatMeetingStagePayload) =>
  encodeURIComponent(JSON.stringify(payload));

export const decodeCADchatMeetingStagePayload = (
  encodedPayload: string | undefined
): CADchatMeetingStagePayload | undefined => {
  if (!encodedPayload) return undefined;

  try {
    return JSON.parse(decodeURIComponent(encodedPayload)) as CADchatMeetingStagePayload;
  } catch {
    return undefined;
  }
};

export const getCADchatMeetingStagePayload = (
  bodyContent: string | null | undefined
): CADchatMeetingStagePayload | undefined => {
  const payloadMatch = (bodyContent ?? "").match(STAGE_PAYLOAD_REGEX);
  return decodeCADchatMeetingStagePayload(payloadMatch?.[1]);
};

export const hasCADchatMeetingStagePayload = (bodyContent: string | null | undefined) =>
  !!getCADchatMeetingStagePayload(bodyContent);

/**
 * Fallback removal for sanitized bodies (Outlook on the web). When PENDING_BLOCK_REGEX can't match
 * because the id/comments were stripped, locate the enclosing <table> around the headline text —
 * which survives sanitization — and strip it. The pending block contains exactly one, un-nested
 * <table>, so the nearest <table>…</table> around the headline is our block.
 */
const removePendingBlockBySentinel = (body: string): string => {
  const sentinelIndex = body.indexOf(CADCHAT_PENDING_MEETING_HEADLINE);
  if (sentinelIndex === -1) return body;

  let start = body.lastIndexOf("<table", sentinelIndex);
  const tableCloseIndex = body.indexOf("</table>", sentinelIndex);
  if (start === -1 || tableCloseIndex === -1) return body;

  let end = tableCloseIndex + "</table>".length;

  // The pending block wraps the table in a <div> (whose id the sanitizer stripped). If that div
  // wraps only our table, remove the wrapper too — otherwise an empty, margin-padded div is left
  // behind and stacks up extra spacing each time the meeting is re-staged after a removal.
  const openDivMatch = body.slice(0, start).match(/<div\b[^>]*>\s*$/i);
  const closeDivMatch = body.slice(end).match(/^\s*<\/div>/i);
  if (openDivMatch && typeof openDivMatch.index === "number" && closeDivMatch) {
    start = openDivMatch.index;
    end += closeDivMatch[0].length;
  }

  return body.slice(0, start) + body.slice(end);
};

export const removeCADchatPendingMeetingBlock = (bodyContent: string | null | undefined) => {
  const body = bodyContent ?? "";

  // Preferred: the attribute/comment-delimited block (kept intact by the Outlook desktop editor).
  if (PENDING_BLOCK_REGEX.test(body)) {
    return body.replace(PENDING_BLOCK_REGEX, "");
  }

  // Fallback: sanitized body (Outlook on the web stripped the id/comments).
  return removePendingBlockBySentinel(body);
};

export const buildCADchatPendingMeetingBlock = (payload: CADchatMeetingStagePayload) => {
  const encodedPayload = encodeCADchatMeetingStagePayload(payload);

  // NOTE: keep this block to a single, un-nested <table> (no inner <div> or nested <table>).
  // PENDING_BLOCK_REGEX matches non-greedily to the first </div>, and the sanitized-body fallback
  // (removePendingBlockBySentinel) strips the enclosing <table>…</table> around the headline — both
  // assume this one-table structure. Inline styles keep it email-safe across the Outlook desktop
  // and web renderers. Styling mirrors the CADchat app: white card, dark title, muted body text,
  // a thin divider, and a warning in the app's red.
  return [
    `<!-- CADCHAT_PENDING_MEETING -->`,
    `<div id="${CADCHAT_PENDING_MEETING_BLOCK_ID}" data-cadchat-stage="${encodedPayload}" style="margin:16px 0;">`,
    `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:520px;border:1px solid #e5e7eb;border-radius:8px;border-collapse:collapse;background-color:#ffffff;font-family:'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">`,
    `<tr>`,
    `<td style="padding:18px 20px 14px 20px;">`,
    `<p style="margin:0;font-size:16px;font-weight:600;color:#111827;">${CADCHAT_PENDING_MEETING_HEADLINE}</p>`,
    `<p style="margin:8px 0 0 0;font-size:14px;line-height:21px;color:#6b7280;">Click <b style="color:#111827;font-weight:600;">Send</b> to create this meeting in CADchat and add the join link to your invitation.</p>`,
    `</td>`,
    `</tr>`,
    `<tr>`,
    `<td style="padding:13px 20px 16px 20px;border-top:1px solid #eceef1;">`,
    `<p style="margin:0;font-size:13px;line-height:19px;color:#b42318;">Heads up — if a Teams meeting (or another online meeting) is turned on for this event, turn it off so your guests join through CADchat.</p>`,
    `</td>`,
    `</tr>`,
    `</table>`,
    `</div>`,
    `<!-- /CADCHAT_PENDING_MEETING -->`,
  ].join("");
};

export const addOrReplaceCADchatPendingMeetingBlock = (
  bodyContent: string | null | undefined,
  payload: CADchatMeetingStagePayload
) => {
  // No <br/> separator: the block carries its own margin. Adding separators here accumulated extra
  // padding each time the meeting was re-staged after a removal.
  const bodyWithoutPendingBlock = removeCADchatPendingMeetingBlock(bodyContent).trimEnd();

  return `${bodyWithoutPendingBlock}${buildCADchatPendingMeetingBlock(payload)}`;
};

export const buildCADchatFinalMeetingBlock = (spaceId: string, spaceName: string) => {
  const meetingLink = getCADchatSpaceLink(spaceId);

  return [
    `<div id="${CADCHAT_FINAL_MEETING_BLOCK_ID}" style="margin:16px 0;">`,
    `<b>CADchat Meeting Information</b>`,
    `<p>`,
    `You've been invited to join a meeting in the space ${escapeHtml(spaceName)}.<br /><br />`,
    `Click here to join the meeting: <a title="Join the meeting in CADchat" href="${escapeHtml(
      meetingLink
    )}">${escapeHtml(meetingLink)}</a>`,
    `</p>`,
    `<p>`,
    `If you don't have a CADchat account, you will be asked to make one to join the call. It's fast and free!`,
    `</p>`,
    `</div>`,
  ].join("");
};

export const removeCADchatFinalMeetingBlock = (bodyContent: string | null | undefined) =>
  (bodyContent ?? "").replace(FINAL_BLOCK_REGEX, "");

export const replaceCADchatPendingMeetingBlockWithFinal = (
  bodyContent: string | null | undefined,
  spaceId: string,
  spaceName: string
) => {
  // Remove the pending block (robust across the desktop editor and the web sanitizer).
  const body = removeCADchatPendingMeetingBlock(bodyContent);

  // If a final block for this space is already present, don't append a duplicate. We match on the
  // space link — which survives the web sanitizer, unlike the id — so editing/re-sending an event
  // that already has a CADchat meeting doesn't add the block a second time.
  const spaceLink = getCADchatSpaceLink(spaceId, false);
  if (spaceLink && body.includes(spaceLink)) {
    return body.trimEnd();
  }

  // Otherwise strip any stale final block (e.g. a different space) and append a fresh one.
  // No <br/> separator: the final block carries its own margin (see buildCADchatFinalMeetingBlock).
  const cleaned = removeCADchatFinalMeetingBlock(body).trimEnd();
  return `${cleaned}${buildCADchatFinalMeetingBlock(spaceId, spaceName)}`;
};
